import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import { LanguageIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface LanguageSwitcherProps {
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className }) => {
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getCurrentLanguageCode = () => {
    return currentLanguage.toUpperCase();
  };

  const getLanguageFlag = (code: string) => {
    switch (code) {
      case 'vi':
        return '🇻🇳';
      case 'en':
        return '🇬🇧';
      default:
        return '';
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        className='flex items-center space-x-1 text-gray-700 hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-gray-100 md:hover:bg-gray-100'
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('common.language')}
        title={t('common.language')}
      >
        <span className='mr-1 text-lg'>{getLanguageFlag(currentLanguage)}</span>
        <span className='text-sm font-medium'>
          {getCurrentLanguageCode()}
        </span>
        <ChevronDownIcon 
          className={`h-3 w-3 ml-1 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {isOpen && (
        <div className='absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200'>
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                currentLanguage === lang.code
                  ? 'text-primary font-medium'
                  : 'text-gray-700'
              } flex items-center`}
              onClick={() => {
                changeLanguage(lang.code);
                setIsOpen(false);
              }}
            >
              <span className='mr-2 text-lg'>{getLanguageFlag(lang.code)}</span>
              <span className='font-medium'>{lang.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
